import axios from 'utils/axiosInstance';
import { useEffect, useState } from 'react';
import { Note, Tag } from 'types/notes';
import NotesIndex2 from './NotesIndex2';
import Banner2 from 'images/golden155/banner2.png';

import clsx from 'clsx';
import timeHelper from 'utils/timeHelper';
import { UserInfo } from 'types/user_info';
import { checkDailyLimit, addViewedNote } from 'utils/dailyViewTracker';
import CommentSection from 'components/comments/CommentSection';

// 무료 멤버십 가입 버튼 컴포넌트
const FreeMembershipJoinButton = () => {
  const [isLoading, setIsLoading] = useState(false);
  const VITE_DAILY_NOTE_VIEW_LIMIT = parseInt(import.meta.env.VITE_DAILY_NOTE_VIEW_LIMIT || '3');

  const handleFreeMembershipJoin = async () => {
    if (!confirm('무료 멤버십에 가입하시겠습니까?\n\n' +
      '- 클릭 한번으로 가입할 수 있습니다.\n' +
      '- 하루 ' + VITE_DAILY_NOTE_VIEW_LIMIT + '개의 황금노트를 읽을 수 있습니다.\n' +
      '- 언제든지 유료 멤버십으로 업그레이드 가능합니다.')) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post('/api/v1/plans/subscribe', {
        plan_key: 'basic'
      });

      if (response.data.success) {
        alert('무료 멤버십에 가입되었습니다!\n페이지를 새로고침합니다.');
        window.location.reload();
      } else {
        alert(response.data.message || '가입 중 오류가 발생했습니다.');
      }
    } catch (error: any) {
      console.error('무료 멤버십 가입 실패:', error);
      const message = error.response?.data?.message || '가입 중 오류가 발생했습니다.';
      alert(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleFreeMembershipJoin}
      disabled={isLoading}
      className="btn btn-primary w-full"
    >
      {isLoading ? (
        <>
          <span className="loading loading-spinner loading-sm"></span>
          가입 중...
        </>
      ) : (
        '무료 멤버십 바로 가입'
      )}
    </button>
  );
};

interface NoteShowProps {
  currentNoteId: string | number;
  userInfo: UserInfo | null;
}

interface ScrapButtonProps {
  noteId: string | number;
  isLoggedIn: boolean;
}

// 스크랩 버튼 컴포넌트
const ScrapButton = ({ noteId, isLoggedIn }: ScrapButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isScrapped, setIsScrapped] = useState(false);

  // 로그인하지 않은 사용자에게는 아무것도 렌더링하지 않음
  if (!isLoggedIn) {
    return null;
  }

  useEffect(() => {
    if (isLoggedIn) {
      checkScrapStatus();
    }
  }, [noteId, isLoggedIn]);

  const checkScrapStatus = async () => {
    try {
      const response = await axios.get(`/api/v1/notes/${noteId}/scrap_status`);
      if (response.data.success) {
        setIsScrapped(response.data.is_scrapped);
      }
    } catch (error) {
      console.error('스크랩 상태 확인 실패:', error);
    }
  };

  const handleScrap = async () => {
    if (!isLoggedIn) {
      alert('로그인이 필요합니다.');
      return;
    }

    setIsLoading(true);

    try {
      const response = isScrapped
        ? await axios.delete(`/api/v1/notes/${noteId}/scrap`)
        : await axios.post(`/api/v1/notes/${noteId}/scrap`);

      if (response.data.success) {
        setIsScrapped(response.data.is_scrapped);
        alert(response.data.message);
      } else {
        alert(response.data.message);
      }
    } catch (error: any) {
      console.error('스크랩 처리 실패:', error);
      const message = error.response?.data?.message || '처리 중 오류가 발생했습니다.';
      alert(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex gap-2">
      <button
        onClick={handleScrap}
        disabled={isLoading}
        className={`btn btn-outline btn-sm ${isScrapped ? 'btn-success' : 'btn-primary'} flex-1`}
      >
        {isLoading ? (
          <>
            <span className="loading loading-spinner loading-sm"></span>
            처리중...
          </>
        ) : (
          <>
            <span className="text-lg">{isScrapped ? '📖' : '📝'}</span>
            {isScrapped ? '내 노트에서 빼기' : '내 노트에 넣기'}
          </>
        )}
      </button>

      <button
        onClick={() => window.location.href = '/profile/my_notes'}
        className="btn btn-outline btn-sm btn-ghost flex-none"
        title="내 노트 보기"
      >
        📚 내 노트
      </button>
    </div>
  );
};

interface ShareButtonProps {
  noteId: string | number;
  isLoggedIn: boolean;
  dailyLimitExceeded?: boolean;
}

// 공유 버튼 컴포넌트
const ShareButton = ({ noteId, isLoggedIn, dailyLimitExceeded }: ShareButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!isLoggedIn) {
    return null;
  }

  const handleShare = async () => {
    if (dailyLimitExceeded) {
      alert('일일 조회 한도를 초과하여 더 이상 공유 링크를 생성할 수 없습니다.\n\n유료 멤버십으로 업그레이드하시면 무제한으로 열람 및 공유가 가능합니다.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`/api/v1/notes/${noteId}/shares`);
      if (response.data.success && response.data.share_url) {
        const shareUrl = response.data.share_url;

        // 클립보드에 복사 시도
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(shareUrl);
          setCopied(true);
          setTimeout(() => setCopied(false), 3000);
          alert('공유 링크가 복사되었습니다!\n\n받는 사람은 회원가입 없이도 24시간 동안 이 노트를 무료로 읽을 수 있습니다.');
        } else {
          // 클립보드 미지원 환경 폴백
          prompt('아래 링크를 복사하여 공유하세요 (24시간 유효):', shareUrl);
        }
      } else {
        alert(response.data.message || '공유 링크를 생성할 수 없습니다.');
      }
    } catch (error: any) {
      console.error('공유 링크 생성 실패:', error);
      const message = error.response?.data?.message || '공유 링크 생성 중 오류가 발생했습니다.';
      alert(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleShare}
      disabled={isLoading}
      className="btn btn-outline btn-sm btn-info flex-1"
      title={dailyLimitExceeded ? "일일 조회 한도를 초과하여 더 이상 공유 링크를 생성할 수 없습니다." : "24시간 무료 공유 링크 생성"}
    >
      {isLoading ? (
        <>
          <span className="loading loading-spinner loading-sm"></span>
          링크 생성중...
        </>
      ) : (
        <>
          <span className="text-lg">🔗</span>
          {copied ? '링크 복사 완료!' : '기사 공유하기'}
        </>
      )}
    </button>
  );
};

// 태그 표시 컴포넌트
interface TagListProps {
  tags: Tag[];
  onTagClick: (tag: Tag) => void;
}

const TagList = ({ tags, onTagClick }: TagListProps) => {
  if (!tags || tags.length === 0) {
    return null;
  }

  return (
    <div className="px-4 sm:px-10 md:px-20 py-6">
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-gray-600 font-semibold">태그:</span>
        {tags.map((tag) => (
          <button
            key={tag.id}
            onClick={() => onTagClick(tag)}
            className="px-3 py-1 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 rounded-full text-sm font-medium transition-colors cursor-pointer"
          >
            #{tag.name}
          </button>
        ))}
      </div>
    </div>
  );
};

const NoteShow = ({ currentNoteId, userInfo }: NoteShowProps) => {
  const searchParams = new URLSearchParams(window.location.search);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState<Note | null>(null);
  const page = searchParams.get('page');
  const shareToken = searchParams.get('share_token');
  const isAdmin = userInfo?.roleinfo?.isAllAdminGroup ?? userInfo?.roleinfo?.is_all_admin_group;
  const hasMembership = userInfo?.membership?.hasActiveMembership ?? userInfo?.membership?.has_active_membership;
  const isLoggedIn = Boolean(window.is_logged_in);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const fetchNote = async (id: string | number) => {
    setLoading(true);

    try {
      const url = shareToken
        ? `/api/v1/notes/${id}?share_token=${encodeURIComponent(shareToken)}`
        : `/api/v1/notes/${id}`;
      const { data } = await axios.get<Note>(url);
      setNote(data);

      // 로그인했고, 성공적으로 조회되고 한도 초과가 아닌 경우에만 로컬 스토리지에 기록
      if (isLoggedIn && !(data as any).daily_limit_exceeded && !data.shared_info?.is_shared && data.id) {
        addViewedNote(data.id);
      }
    } catch (e: any) {
      console.error(e);
      // 에러 발생 시 기본 에러 처리
      setNote(null);
    } finally {
      setLoading(false);
    }
  };

  const handleTagClick = (tag: Tag) => {
    // 태그 검색 페이지로 이동
    window.location.href = `/notes?tag=${tag.slug}`;
  };

  useEffect(() => {
    fetchNote(currentNoteId);
  }, [currentNoteId, shareToken]);

  if (loading) {
    return (
      <div className='flex justify-center px-4 py-30'>
        <span className="loading loading-spinner sm:loading-sm lg:loading-lg"></span>
      </div>
    );
  }

  if (!note) {
    // note가 null인 경우 (데이터를 찾지 못했거나 아직 로딩되지 않은 상태)
    return <div className="flex justify-center px-4 py-30">노트를 찾을 수 없습니다.</div>;
  }

  const isSharedView = Boolean(note.shared_info?.is_shared);
  const isExpiredShare = Boolean(note.shared_info?.expired);
  const shouldLockContent =
    note.content_locked ??
    (!isSharedView && (!window.is_logged_in || !hasMembership || Boolean(note.daily_limit_exceeded)));

  return (
    <div>
      <div className="pt-10">
        {/* 공유 링크를 통해 접속한 사용자용 안내 배너 */}
        {isSharedView && note.shared_info && (
          <div className="mb-6 px-4 sm:px-10 md:px-20">
            <div className="alert alert-info shadow-md bg-sky-50 border-sky-200 text-sky-950 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 rounded-xl">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🎁</span>
                <div>
                  <div className="font-bold text-base">
                    <strong>{note.shared_info.sender_name}</strong> 님이 공유해주신 황금노트입니다.
                  </div>
                  <div className="text-xs text-sky-800">
                    회원가입 없이 24시간 동안 전문을 무료로 읽으실 수 있습니다. (남은 시간: 약 {note.shared_info.remaining_hours}시간)
                  </div>
                </div>
              </div>
              {!isLoggedIn && (
                <a
                  href="/signup_check"
                  className="btn btn-sm btn-primary text-white shrink-0 self-end sm:self-auto"
                >
                  무료 회원가입
                </a>
              )}
            </div>
          </div>
        )}

        {/* 만료된 공유 링크 접속 안내 배너 */}
        {isExpiredShare && (
          <div className="mb-6 px-4 sm:px-10 md:px-20">
            <div className="alert alert-warning shadow-sm p-4 rounded-xl flex items-center gap-3 text-sm">
              <span className="text-xl">⚠️</span>
              <div>
                공유 링크의 <strong>유효 기간(24시간)이 만료</strong>되었습니다. 계속 읽으시려면 회원가입 또는 로그인이 필요합니다.
              </div>
            </div>
          </div>
        )}

        <div className="sm:px-10 md:px-20">
          <div className="bg-black text-white text-sm font-bold py-1 px-3 inline-block mb-6">
            <a href={`/notes?pboard=${note.pbid}`}>
              오늘의 {note.pboard_name === '황금노트' ? '황금노트' : (note.pboard_name || '황금노트').replace(/^황금/, '')}
            </a>
          </div>

          <h1 className="text-3xl font-bold mb-4">
            {note.title}
          </h1>

          <div className="text-gray-500 text-sm mb-6 flex items-center space-x-2">
            {note.author && <span>{note.author}</span>}
            {note.published_at &&
              <span>{timeHelper.timestr(note.published_at, 3)}</span>
            }
          </div>
        </div>

        <div className='content-wrap relative px-0 py-10 sm:px-10 md:px-20 sm:py-20'>
          <div
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: note.content }}
          />

          {/* 비공유 일반 열람 시 잠금 UI */}
          {shouldLockContent && (
            <>
              <div className='fader'></div>
              <div className='w-full z-[1000] relative'>
                {!window.is_logged_in ? (
                  <>
                    <div className='flex justify-center px-4 pt-5 py-10'>
                      <div>
                        <div className='text-center text-2xl font-bold'>지금 가입하고 <br />인사이트를 쌓아보세요.</div>
                        <br />
                        <a href='/signup_check' className="btn btn-primary w-full">회원 가입하기</a>
                      </div>
                    </div>
                    <p className='flex justify-center w-full text-xs'>※ 멤버십 회원만 모든 황금노트를 확인할 수 있습니다. 무료회원으로 가입해보세요.(하루 조회수 제한있음)</p>
                  </>
                ) : window.is_logged_in && !hasMembership ? (
                  <>
                    <div className='flex justify-center px-4 pt-5 py-10'>
                      <div className="w-full max-w-sm">
                        <div className='text-center text-2xl font-bold'>멤버십 구독으로 <br />모든 황금노트를 확인하세요.</div>
                        <br />
                        <div className="space-y-3">
                          <FreeMembershipJoinButton />
                          <a href='/select_plan' className="btn btn-outline btn-primary w-full">유료 멤버십 업그레이드</a>
                        </div>
                      </div>
                    </div>
                    <p className='flex justify-center w-full text-xs'>※ 멤버십 회원이 되면 모든 황금노트를 확인할 수 있습니다.</p>
                  </>
                ) : note.daily_limit_exceeded ? (
                  <>
                    <div className='flex justify-center px-4 pt-5 py-10'>
                      <div className="w-full max-w-sm">
                        <div className='text-center text-2xl font-bold'>일일 조회 한도 초과</div>
                        <br />
                        <div className='text-center text-lg mb-4'>
                          무료 멤버십(준회원)은 하루에 {note.limit}개의 노트만 조회할 수 있습니다.
                        </div>
                        <div className="text-center text-sm text-gray-600 mb-6">
                          오늘 조회한 노트: {note.current_count}/{note.limit}개
                        </div>
                        <a href='/select_plan' className="btn btn-primary w-full">유료 멤버십 업그레이드</a>
                      </div>
                    </div>
                    <p className='flex justify-center w-full text-xs'>※ 실버/골드 멤버십으로 업그레이드하면 제한없이 조회가 가능합니다.</p>
                  </>
                ) : null}
              </div>
            </>
          )}

          {/* 공유 링크를 통해 본문을 다 읽은 비회원 대상 가입 유도 배너 */}
          {isSharedView && !isLoggedIn && (
            <div className="mt-12 p-6 bg-amber-50 rounded-2xl border border-amber-200 text-center max-w-2xl mx-auto">
              <div className="text-2xl font-bold text-gray-900 mb-2">황금노트의 더 많은 인사이트가 궁금하신가요?</div>
              <p className="text-gray-600 text-sm mb-5">
                무료 멤버십에 가입하시면 매일 엄선된 황금노트와 다양한 독서클럽 콘텐츠를 즐기실 수 있습니다.
              </p>
              <div className="flex justify-center gap-3">
                <a href="/signup_check" className="btn btn-primary">
                  간편 무료 회원가입
                </a>
                <a href="/notes" className="btn btn-outline">
                  다른 노트 둘러보기
                </a>
              </div>
            </div>
          )}
        </div>

        {/* 태그 목록 */}
        {note.tags && note.tags.length > 0 && (
          <div className='mb-6'>
            <TagList tags={note.tags} onTagClick={handleTagClick} />
          </div>
        )}

        {/* 스크랩 및 공유 버튼 (로그인한 사용자) */}
        {isLoggedIn && (
          <div className="px-4 sm:px-10 md:px-20 pt-10 pb-6 mb-10">
            <div className="min-w-[280px] max-w-[420px] mx-auto flex flex-col sm:flex-row gap-2">
              <ShareButton
                noteId={note.uid || note.id}
                isLoggedIn={isLoggedIn}
                dailyLimitExceeded={note.daily_limit_exceeded}
              />
              <ScrapButton noteId={note.id} isLoggedIn={isLoggedIn} />
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="float-right text-right mt-[-20px]">
            <div className='w-full flex justify-end max-h-16 overflow-hidden'>
              <a href={`/admin/pposts?pbid=${note.pbid}`}><img src="/img/common/admin.png" /></a>&nbsp;
              <a href={`/admin/pposts/${note.id}/edit`}><img src="/img/common/admin.png" /></a>&nbsp;
            </div>
          </div>
        )
        }

        <div className="px-4 sm:px-10 md:px-20 pb-10">
          <CommentSection noteId={note.id} isLoggedIn={isLoggedIn} userInfo={userInfo} />
        </div>

        <div className="pb-10 px-0 sm:px-4">
          <NotesIndex2 currentNoteId={note.id} page={parseInt(page || '1')} />
        </div>

        {/* 황금독서클럽 카카오채널 배너 */}
        <div className="px-0 sm:px-5 md:px-10 pb-0 sm:pb-20">
          <a
            href="https://pf.kakao.com/_HPSJn"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full"
          >
            <div className="bg-gradient-to-r from-yellow-300 to-yellow-400 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 text-center">
              <div className="flex items-center justify-center gap-4">
                <div className="text-4xl">💬</div>
                <div className="text-black">
                  <h3 className="text-sm sm:text-xl md:text-xl font-bold mb-1">황금독서클럽 카카오채널</h3>
                  <p className="text-xs sm:text-sm opacity-80">독서클럽 소식과 정보를 카카오톡으로 받아보세요!</p>
                </div>
                <div className="text-2xl">→</div>
              </div>
            </div>
          </a>
        </div>
      </div>

      {/* 맨 위로 가기 버튼 */}
      {showScrollTop && (
        <button
          type="button"
          onClick={scrollToTop}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 p-2 sm:p-3 bg-white/90 hover:bg-white text-gray-800 rounded-full shadow-lg hover:shadow-xl border border-gray-200 transition-all duration-300 transform hover:-translate-y-1 flex items-center justify-center cursor-pointer group focus:outline-none"
          title="맨 위로 가기"
          aria-label="맨 위로 가기"
        >
          <svg
            className="w-4 h-4 sm:w-6 sm:h-6 transform group-hover:-translate-y-0.5 transition-transform duration-200 text-gray-700"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}
    </div>
  );
}

export default NoteShow;
